import React, { useState } from "react";
import { Facebook, Twitter, Mail, Link2, Check } from "lucide-react";
import { toast } from "sonner";

// WhatsApp & Pinterest don't exist as distinct lucide icons in older versions —
// use inline SVGs for brand fidelity.
const WhatsappIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M20.52 3.48A11.88 11.88 0 0 0 12.06 0C5.5 0 .17 5.33.17 11.9a11.85 11.85 0 0 0 1.65 6.04L0 24l6.26-1.63a11.9 11.9 0 0 0 5.8 1.48h.01c6.56 0 11.89-5.33 11.89-11.9 0-3.18-1.24-6.17-3.44-8.47zM12.06 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.71.97.99-3.61-.24-.37a9.86 9.86 0 0 1-1.52-5.3c0-5.45 4.44-9.9 9.89-9.9 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.9 6.99c0 5.46-4.44 9.9-9.9 9.9zm5.43-7.41c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.76.97-.94 1.17-.17.2-.35.22-.65.08-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.66-2.06-.17-.3-.02-.46.13-.61.14-.13.3-.34.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.2-.24-.59-.49-.5-.67-.5h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

const PinterestIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.08 3.17 9.42 7.64 11.16-.11-.95-.2-2.4.04-3.43.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.02 0 1.52.77 1.52 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.17 1.78 2.17 2.14 0 3.78-2.25 3.78-5.5 0-2.88-2.07-4.89-5.02-4.89-3.42 0-5.43 2.57-5.43 5.22 0 1.03.4 2.14.9 2.74.1.12.11.22.08.34l-.34 1.36c-.05.22-.18.27-.41.16-1.52-.71-2.47-2.93-2.47-4.71 0-3.83 2.78-7.36 8.02-7.36 4.21 0 7.48 3 7.48 7.01 0 4.18-2.64 7.55-6.3 7.55-1.23 0-2.39-.64-2.79-1.4l-.76 2.89c-.27 1.06-1.02 2.38-1.52 3.19A12 12 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
  </svg>
);

export default function ShareButtons({ url, title, description, compact = false }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareTitle = title || "Globezing — Travel stories from the slow road";
  const shareText = description || shareTitle;

  const enc = encodeURIComponent;
  const targets = [
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      icon: <Facebook size={compact ? 14 : 16} />,
      testid: "share-facebook",
      color: "hover:text-[#1877F2]",
    },
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?text=${enc(shareTitle)}&url=${enc(shareUrl)}`,
      icon: <Twitter size={compact ? 14 : 16} />,
      testid: "share-twitter",
      color: "hover:text-[#1DA1F2]",
    },
    {
      name: "WhatsApp",
      href: `https://wa.me/?text=${enc(`${shareTitle} — ${shareUrl}`)}`,
      icon: <WhatsappIcon />,
      testid: "share-whatsapp",
      color: "hover:text-[#25D366]",
    },
    {
      name: "Pinterest",
      href: `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&description=${enc(shareText)}`,
      icon: <PinterestIcon />,
      testid: "share-pinterest",
      color: "hover:text-[#E60023]",
    },
    {
      name: "Email",
      href: `mailto:?subject=${enc(shareTitle)}&body=${enc(`${shareText}\n\n${shareUrl}`)}`,
      icon: <Mail size={compact ? 14 : 16} />,
      testid: "share-email",
      color: "hover:text-[#9c4122]",
    },
  ];

  const onCopy = async () => {
    try {
      if (navigator.share && compact) {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const dim = compact ? "w-8 h-8" : "w-10 h-10";

  return (
    <div
      data-testid="share-buttons"
      className={`flex items-center ${compact ? "gap-1" : "gap-2"} flex-wrap`}
    >
      {!compact && (
        <span className="kicker text-stone-500 pr-1">Share</span>
      )}
      {targets.map((t) => (
        <a
          key={t.name}
          href={t.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share on ${t.name}`}
          data-testid={t.testid}
          className={`${dim} flex items-center justify-center border border-stone-300 text-stone-700 transition-colors ${t.color} hover:border-current`}
        >
          {t.icon}
        </a>
      ))}
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy link"
        data-testid="share-copy-link"
        className={`${dim} flex items-center justify-center border border-stone-300 text-stone-700 transition-colors hover:text-stone-950 hover:border-stone-950`}
      >
        {copied ? <Check size={compact ? 14 : 16} /> : <Link2 size={compact ? 14 : 16} />}
      </button>
    </div>
  );
}
